import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { MAX_SAVED_SEARCH_NAME_LENGTH } from '~/common/constants/max_lengths';
import { EntityVisibilitySelect } from '~/components/Shared/EntityVisibilitySelect/EntityVisibilitySelect';
import { EntityVisibilityType } from '~/components/Shared/EntityVisibilitySelect/types';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { SaveSearchPermissionType } from '~/enums/SaveSearchPermissionType';

type Config = { name: string } & EntityVisibilityType;

type Props = {
  /** modal title */
  title: string;
  /** placeholder of textbox */
  placeholder: string;
  isVisible: boolean;
  onSubmit: (params: Config) => void;
  onHide: () => void;
  initialValues?: Config;
};

export const SaveEditEntityModal = ({
  initialValues,
  isVisible,
  placeholder,
  title,
  onHide,
  onSubmit,
}: Props) => {
  const intl = useIntl();
  const {
    name: originalName,
    visibility = SaveSearchPermissionType.Private,
    departmentIds = [],
    userIds = [],
  } = initialValues ?? {};
  const [name, setName] = useState(originalName ?? null);
  const [entityPermission, setEntityPermission] =
    useState<EntityVisibilityType>({
      visibility: visibility,
      departmentIds,
      userIds,
    });

  const isMissingUserDepartments =
    entityPermission.visibility === SaveSearchPermissionType.Shared &&
    (!entityPermission.userIds || entityPermission.userIds?.length === 0) &&
    (!entityPermission.departmentIds ||
      entityPermission.departmentIds?.length === 0);

  const onActionClick = () => {
    if (name) {
      onSubmit({ name, ...entityPermission });
    }
  };

  const reset = () => {
    onHide();
    setName(originalName ?? null);
    setEntityPermission({
      visibility: visibility,
      departmentIds,
      userIds,
    });
  };

  const isNameLimitExceeded =
    (name ?? '').length > MAX_SAVED_SEARCH_NAME_LENGTH;

  const primaryAction = {
    text: intl.formatMessage({
      id: 'SaveEditEntityModal.saveButton',
      defaultMessage: 'Save',
      description: 'Save button text for save/edit entity modal',
    }),
    onClick: onActionClick,
    disabled: !name || isMissingUserDepartments || isNameLimitExceeded,
    tooltip: isNameLimitExceeded
      ? intl.formatMessage(
          {
            id: 'SaveEditEntityModal.nameLimitTooltip',
            defaultMessage: 'The name cannot exceed {maxLength} characters.',
            description:
              'Tooltip when name exceeds character limit. Example: maxLength = 255',
          },
          { maxLength: MAX_SAVED_SEARCH_NAME_LENGTH },
        )
      : '',
  };

  return (
    <Modal
      isVisible={isVisible}
      title={title}
      overflow="visible"
      primaryAction={primaryAction}
      onHide={reset}
    >
      <Layout preset="form-fields">
        <FormField<string, false>
          name="nameField"
          label={intl.formatMessage({
            id: 'SaveEditEntityModal.nameLabel',
            defaultMessage: 'Name',
            description: 'Label for name input field',
          })}
          placeholder={placeholder}
          value={name}
          input={TextInput}
          onChange={setName}
        />
        <EntityVisibilitySelect
          label={intl.formatMessage({
            id: 'SaveEditEntityModal.visibilityLabel',
            defaultMessage: 'Visibility',
            description: 'Label for visibility selection field',
          })}
          value={entityPermission}
          onChange={setEntityPermission}
        />
      </Layout>
    </Modal>
  );
};
